/*
 * Copyright (c) 2019 Signum+ Software & Facility GmbH
 * Author: jahn@signum-plus.at
 */

import { Observable, BehaviorSubject, Subscription } from 'rxjs';
import {
  ApplicationError,
  ObservableMap,
  ChangedEvent,
  ObservableModel,
  ObservableModelData,
  Change,
  forEach,
  ObservableCollectionOptions,
  isEmpty,
  toEntries,
  toArray,
  ChangeOptions,
  ObservableCollection
} from '@typescript-kit/core';
import { ViewComponentKey } from '../key/view-component.key';
import { ViewErrorKey } from '../key/view-error.key';

export interface ComponentData extends ObservableModelData {
  parent?: ComponentData;
  key?: string;
  type?: string;
  isHidden?: boolean;
  isDisabled?: boolean;
  isFocused?: boolean;
  isActive?: boolean;
  tabIndex?: number;
  tags?: string | string[] | { [tag: string]: any };
  onChanged?: (event: ChangedEvent, model: ComponentData) => void;
  onClick?: (event: any, model: ComponentData) => void;
  onKeydown?: (event: any, model: ComponentData) => any;
}

export class ComponentFieldKey {
  static readonly KEY = 'key';
  static readonly TYPE = 'type';
  static readonly IS_HIDDEN = 'isHidden';
  static readonly IS_DISABLED = 'isDisabled';
  static readonly IS_FOCUSED = 'isFocused';
  static readonly IS_ACTIVE = 'isActive';
  static readonly TAB_INDEX = 'tabIndex';
  static readonly TAGS = 'tags';
  static readonly ON_CHANGED = 'onChanged';
  static readonly ON_CLICK = 'onClick';
  static readonly ON_KEYDOWN = 'onKeydown';
}

export class ComponentModel extends ObservableModel implements ComponentData {
  static readonly KEYS = Object.keys(ComponentFieldKey).map((key) => ComponentFieldKey[key]);
  private static readonly lastFocusedSubject = new BehaviorSubject<ComponentModel>(null);
  protected childrenChangedSubscription: Subscription;
  protected _parent: ComponentModel;
  protected _children: Iterable<ComponentModel>;
  protected _childrenKeys: string[];

  constructor(data?: ComponentData, keys?: string[], observeOptions?: ObservableCollectionOptions) {
    super(data, keys !== undefined ? keys : ComponentModel.KEYS, observeOptions);
  }

  static get lastFocused(): ComponentModel {
    return ComponentModel.lastFocusedSubject.value;
  }

  static get lastFocusedChanged(): Observable<ComponentModel> {
    return ComponentModel.lastFocusedSubject.asObservable();
  }

  get parent(): ComponentModel {
    return this._parent;
  }

  get children(): Iterable<ComponentModel> {
    if (!this._children) {
      this._children = this.createChildrenIterable();
    }
    return this._children;
  }

  get key(): string {
    return this.get(ComponentFieldKey.KEY);
  }

  get type(): string {
    return this.get(ComponentFieldKey.TYPE);
  }

  get isHidden(): boolean {
    return this.get(ComponentFieldKey.IS_HIDDEN, false);
  }

  set isHidden(value: boolean) {
    this.set(ComponentFieldKey.IS_HIDDEN, value);
  }

  get isDisabled(): boolean {
    return this.get(ComponentFieldKey.IS_DISABLED, this.parent ? this.parent.isDisabled : false);
  }

  set isDisabled(value: boolean) {
    this.set(ComponentFieldKey.IS_DISABLED, value);
  }

  get isFocused(): boolean {
    return this.get(ComponentFieldKey.IS_FOCUSED, false);
  }

  set isFocused(value: boolean) {
    this.set(ComponentFieldKey.IS_FOCUSED, value);
  }

  get isActive(): boolean {
    return this.get(ComponentFieldKey.IS_ACTIVE, false);
  }

  set isActive(value: boolean) {
    this.set(ComponentFieldKey.IS_ACTIVE, value);
  }

  get tabIndex(): number {
    return this.get(ComponentFieldKey.TAB_INDEX);
  }

  set tabIndex(value: number) {
    this.set(ComponentFieldKey.TAB_INDEX, value);
  }

  get tags(): ObservableMap<any> {
    return this.get(ComponentFieldKey.TAGS);
  }

  get onChanged(): (event: ChangedEvent, model: ComponentModel) => void {
    return this.get(ComponentFieldKey.ON_CHANGED);
  }

  get onClick(): (event: any, model: ComponentModel) => void | boolean {
    return this.get(ComponentFieldKey.ON_CLICK);
  }

  get onKeydown(): (event: any, model: ComponentModel) => any {
    return this.get(ComponentFieldKey.ON_KEYDOWN);
  }

  protected createChildrenIterable(): Iterable<ComponentModel> {
    return {
      [Symbol.iterator](): Iterator<ComponentModel> {
        return {
          next: () => ({ done: true, value: undefined })
        };
      }
    };
  }

  protected setupValues(values: ComponentData, exclusive: boolean): ComponentData {
    if (exclusive && !(ComponentFieldKey.TYPE in values)) {
      values.type = ViewComponentKey.BASE;
    }
    this.setupTags(values, exclusive);
    return super.setupValues(values, exclusive) as ComponentData;
  }

  protected setupTags(values: ComponentData, exclusive: boolean): void {
    if (!exclusive && !(ComponentFieldKey.TAGS in values) || values.tags instanceof ObservableMap) {
      return;
    }
    const tags = ComponentModel.toTagsObject(values.tags);
    if (this.tags) {
      this.tags.setValues(tags, { exclusive: true });
      values.tags = this.tags;
    } else {
      values.tags = new ObservableMap(tags);
    }
  }

  protected setupChildren(key: string, values: ComponentData, exclusive: boolean, required = true): void {
    // TODO: use component model factory
    // TODO: re-use existing observable map?
    if (!exclusive && !(key in values)) {
      return;
    }

    let children: ObservableMap<any>;
    if (values[key] instanceof ObservableCollection) {
      if (this.get(key) === values[key]) {
        return;
      }
      children = values[key];
    } else if (!values[key]) {
      if (!required) {
        return;
      }
      children = new ObservableMap();
    } else if (Array.isArray(values[key])) {
      const dataMap = new Map<string, any>();
      values[key].forEach((child, index) => {
        dataMap.set((child && !isEmpty(child.key)) ? child.key : index.toString(), child);
      });
      children = new ObservableMap(dataMap);
    } else {
      const dataMap = new Map<string, any>();
      for (const entry of toEntries(values[key])) {
        dataMap.set(entry[0].toString(), entry[1]);
      }
      children = new ObservableMap(dataMap);
    }
    if (!this._childrenKeys) {
      this._childrenKeys = [key];
    } else if (!this._childrenKeys.includes(key)) {
      this._childrenKeys.push(key);
    }
    if (this.childrenChangedSubscription) {
      this.childrenChangedSubscription.unsubscribe();
    }
    this.childrenChangedSubscription = children.changedEvent
      .subscribe((event) => this.onChildrenPropertyChanged(key, event));
    values[key] = children;
  }

  protected setupChild(key: string, values: ComponentData, exclusive: boolean) {
    // TODO: use component model factory
    if (!exclusive && !(key in values)) {
      return;
    }
    this.resetChildParent(this.get(key));
    this.setChildParent(values[key]);
  }

  protected onChangedValues(changes: { [key: string]: Change<any> }, options: ChangeOptions): void {
    if (this._childrenKeys) {
      this._childrenKeys.forEach((key) => {
        if (!changes[key]) {
          return;
        }
        this.onChildrenInstanceChanged(key, changes[key].originalValue, changes[key].currentValue);
      });
    }
    super.onChangedValues(changes, options);
  }

  protected onChildrenInstanceChanged(
    key: string,
    originalChildren: ObservableCollection<any, ComponentModel>,
    currentChildren: ObservableCollection<any, ComponentModel>
  ): void {
    if (originalChildren) {
      const addedChildren: Array<[string, ComponentModel]> = [];
      const movedChildren: Array<[string, ComponentModel]> = [];
      const removedChildren: Array<[string, ComponentModel]> = [];
      originalChildren.forEach((child, childKey) => {
        if (!currentChildren.firstEntry((value) => value === child)) {
          removedChildren.push([childKey, child]);
        }
      });
      currentChildren.forEach((child, childKey) => {
        if (originalChildren.firstEntry((value) => value === child)) {
          movedChildren.push([childKey, child]);
        } else {
          addedChildren.push([childKey, child]);
        }
      });
      this.onChildrenChanged(key, addedChildren, removedChildren, movedChildren);
    } else {
      this.onChildrenChanged(key, toArray(currentChildren.entries()), [], []);
    }
    return;
  }

  protected onChildrenPropertyChanged(key: string, event: ChangedEvent): void {
    if (event.originalEvent !== event) {
      return;
    }
    const originalChildren = new Set();
    const currentChildren = new Set();
    const addedEntries: Array<[string, ComponentModel]> = [];
    const movedEntries: Array<[string, ComponentModel]> = [];
    const removedEnties: Array<[string, ComponentModel]> = [];
    const changes = event.changes;
    const changedKeys = Object.keys(changes);
    for (const changedKey of changedKeys) {
      const change = changes[changedKey];
      if (change.originalValue !== undefined) {
        originalChildren.add(change.originalValue);
      }
      if (change.currentValue !== undefined) {
        currentChildren.add(change.currentValue);
      }
    }
    for (const changedKey of changedKeys) {
      const change = changes[changedKey];
      if (change.originalValue !== undefined && !currentChildren.has(change.originalValue)) {
        removedEnties.push([change[0], change.originalValue]);
      }
      if (change.currentValue !== undefined && !originalChildren.has(change.currentValue)) {
        if (originalChildren.has(change.currentValue)) {
          movedEntries.push([change[0], change.currentValue]);
        } else {
          addedEntries.push([change[0], change.currentValue]);
        }
      }
    }
    this.onChildrenChanged(key, addedEntries, removedEnties, movedEntries);
  }

  protected onChildrenChanged(
    key: string,
    addedEntries: Array<[string, ComponentModel]>,
    removedEntries: Array<[string, ComponentModel]>,
    movedEntries: Array<[string, ComponentModel]>
  ): void {
    removedEntries.forEach((entry) => this.resetChildParent(entry[1]));
    addedEntries.forEach((entry) => this.setChildParent(entry[1]));
  }

  protected setChildParent(child: any) {
    if (!(child instanceof ComponentModel)) {
      return;
    }
    if (child._parent && child._parent !== this) {
      throw new ApplicationError({
        key: ViewErrorKey.COMPONENT_PARENT_ALREADY_ASSIGNED,
        arguments: { parent: this, child }
      });
    }
    child._parent = this;
  }

  protected resetChildParent(child: any) {
    if (!(child instanceof ComponentModel)) {
      return;
    }
    child._parent = undefined;
  }

  protected fireChangedEvent(changedEvent: ChangedEvent) {
    if (changedEvent.changes['isFocused'] && changedEvent.changes['isFocused'].currentValue) {
      ComponentModel.lastFocusedSubject.next(this);
    }
    if (this.onChanged) {
      this.onChanged(changedEvent, this);
    }
    super.fireChangedEvent(changedEvent);
    if (changedEvent.changes['isDisabled']) {
      this.fireChildrenIsDisabledChangedEvent(this, changedEvent);
    }
  }

  protected fireChildrenIsDisabledChangedEvent(model: ComponentModel, originalEvent: ChangedEvent) {
    const change = originalEvent.changes['isDisabled'] as Change<boolean>;
    forEach(model.children, (child: ComponentModel) => {
      if (child.get('isDisabled', false) !== change.currentValue) {
        child.fireChangedEvent(new ChangedEvent({
          source: child,
          changes: { isDisabled: change },
          originalEvent
        }));
      }
      this.fireChildrenIsDisabledChangedEvent(child, originalEvent);
    });
  }

  public static toTagsObject(source: string | string[] | { [tag: string]: any }): { [tag: string]: any } {
    let tags: object;
    if (!source) {
      tags = null;
    } else if (typeof source === 'string') {
      tags = { [source]: true };
    } else if (Array.isArray(source)) {
      tags = {};
      source.forEach((tag) => tags[tag] = true);
    } else if (typeof source === 'object') {
      tags = source;
    } else {
      const tag: string = JSON.stringify(source);
      tags = { [tag]: true };
    }
    return tags;
  }

}
